<script>
import axios from 'axios'

export default {
  props: {
    camera: {
      type: Object,
      required: true,
    },
    isOnline: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      videoPolling: null,
      videoPollingCount: 0,
      videoURL: null,
      videoMessage: null,
      temperaturePolling: null,
      temperatures: [],
      loading: false,
      loadingColor: this.$style['color-primary'],
      loadingSize: '100px',
    }
  },
  mounted() {
    if (this.camera.isOnline) {
      this.requestVideo()
      this.videoPolling = setInterval(this.requestVideo, 10000)
    }
  },
  beforeDestroy() {
    if (this.videoPolling) {
      clearInterval(this.videoPolling)
    }

    if (this.temperaturePolling) {
      clearInterval(this.temperaturePolling)
    }
  },
  methods: {
    requestVideo: function() {
      axios
        .get(`/api/cameras/getlivevideo/` + this.camera.id)
        .then((response) => {
          if ('restreamer' in response.data) {
            this.videoURL =
              'https://' +
              response.data.restreamer.fullLink +
              '?autoplay=1&mute=1&color=f00&playinline=1'
            this.requestTemperatures()
            this.temperaturePolling = setInterval(
              this.requestTemperatures,
              15000
            )
            clearInterval(this.videoPolling)
            this.loading = false
          } else {
            this.videoPollingCount++

            if (this.videoPollingCount === 20) {
              clearInterval(this.videoPolling)
              this.videoMessage = 'Failed to establish a stable video connection, please try again later.'
              this.loading = false
            }
          }
        })
    },
    requestTemperatures: function() {
      axios
        .get(`/api/cameras/gettemperaturebyzone/` + this.camera.id)
        .then((response) => {
          if ('AlarmZone' in response.data) {
            this.temperatures = response.data.AlarmZone
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
  },
}
</script>

<template>
  <div :class="$style.cameraInfo">
    <div :class="$style.toolbar">
      <BaseLink name="home" :class="$style.button">
        <svg width="24" height="24" viewBox="0 0 7 12" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M5.253.301a1.019 1.019 0 011.447 0c.4.402.4 1.052 0 1.453L2.47 6l4.23 4.246A1.03 1.03 0 015.977 12c-.262 0-.524-.1-.724-.301L.3 6.726a1.033 1.033 0 010-1.452L5.253.3z" fill="currentColor"/></svg>
        Overview
      </BaseLink>
      <BaseLink name="protocol" :params="{ siteId: camera.site_uuid }" :class="[$style.button, $style.infoButton]">Site info & protocol</BaseLink>

    </div>
    <header :class="$style.header">
      <BaseCircle size="xlarge"
        ><svg width="24" height="24" viewBox="0 0 22 15" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M15.128 0c1.026 0 1.862.803 1.862 1.791V3.57l3.664-1.752a.967.967 0 01.905.041.887.887 0 01.441.761v9.758c0 .31-.167.598-.441.762a.965.965 0 01-.905.04l-3.664-1.753v1.782c0 .988-.836 1.791-1.862 1.791H1.862C.836 15 0 14.197 0 13.209V1.791C0 .803.836 0 1.862 0zm0 1.791H1.862v11.418H15.13v-2.905a.854.854 0 01-.09-.365V5.058c0-.129.036-.25.09-.364l-.002-2.903zM12.13 9.474c.502 0 .908.353.908.79 0 .436-.406.789-.908.789H4.982c-.502 0-.908-.353-.908-.79 0-.436.406-.79.908-.79zm8.009-5.408L16.99 5.57v3.856l3.148 1.507V4.066z" fill="currentColor"/></svg></BaseCircle>
      <div :class="$style.headerInfo">
        <h2>{{ camera.name }}</h2>
        <div :class="$style.subtitle">
          <BaseStatus :online="isOnline"></BaseStatus>
          <span v-if="camera.lastDateTimeViewed">
            | Last viewed:
            {{ camera.lastDateTimeViewed | moment('from', 'now') }}
          </span>
        </div>
      </div>
    </header>
    <div :class="$style.content">
      <img :src="`/stream/system/${camera.uuid}/stream.jpg`" style="min-width: 100%; min-height: 100%">
      <BounceLoader :loading="loading" :color="loadingColor" :size="loadingSize" :class="$style.iframeLoading"></BounceLoader>
    </div>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.cameraInfo {
  position: relative;
  padding: 69px 18px 18px 18px;
  margin-bottom: 16px;
  background: $color-white;
  border-radius: 0 0 35px 0;

  @media (min-width: $breakpoint-md) {
    padding: 26px 59px 26px 59px;
  }

  .header {
    display: flex;
    align-items: center;
    margin-bottom: 26px;

    @media (min-width: $breakpoint-md) {
      margin-bottom: 36px;
    }

    .headerInfo {
      margin: 0 0 0 13px;

      @media (min-width: $breakpoint-md) {
        margin: 0 0 0 24px;
      }

      h2 {
        margin: 0;
        font-size: 18px;
        letter-spacing: -0.59px;

        @media (min-width: $breakpoint-md) {
          font-size: 26px;
        }
      }

      .subtitle {
        font-size: 13px;
        color: transparentize($color-text, 0.45);

        @media (min-width: $breakpoint-md) {
          margin-top: 16px;
        }
      }
    }
  }

  .content {
    padding: 0 40px;
    margin: 0 -18px -18px -18px;
    background: $color-gray;
    border-radius: 0 0 35px 0;

    @media (min-width: $breakpoint-md) {
      padding: 0 140px;
      margin: 0 -59px -26px -59px;
    }

    .iframeContainer {
      position: relative;
      display: block;
      height: 0;
      padding: 0 0 75% 0;
      overflow: hidden;

      .videoMessage {
        position: absolute;
        top: 45%;
        right: 0;
        left: 0;
        color: $color-primary;
        text-align: center;
      }

      .temperatures,
      .iframe {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        display: block;
        width: 100%;
        height: 100%;
        border: 0;

        .temperature {
          float: left;
          width: 33.33%;
          height: 50%;

          &:nth-child(-n + 2) {
            border-right: 1px dashed $color-white;
          }

          &:nth-child(n + 4) {
            border-top: 1px dashed $color-white;
            border-right: 1px dashed $color-white;
          }

          &:nth-child(6) {
            border-right: 0;
          }

          span {
            display: inline-block;
            padding: 12px;
            font-size: 13px;
            color: $color-white;
            letter-spacing: -0.43px;
          }
        }
      }

      .temperatures {
        z-index: 998;
        pointer-events: none;
      }

      .iframeLoading {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
    }
  }

  .toolbar {
    display: flex;
    margin-bottom: 2rem;

    .infoButton {
      margin-left: auto;
    }

    @media (min-width: $breakpoint-md) {
      top: 30px;
      left: 64px;
    }
  }
}
</style>
